import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { ContactRequest } from '../contact-request';
import { ContactRequestSource } from '../contact-request-source';
import { Country } from '../country';
import { HttpService } from '../service/http.service';
import { InterestLevel } from '../interest-level';
import { Language } from '../language';
import { LeadType } from '../lead-type';
import { SalesAgent } from '../sales-agent';
import { SchoolListItem } from '../school-list-item';
import { UserForBackend } from '../user-for-backend';
import { SimpleSchoolWithCountries } from '../simple-school-with-countries';

@Component({
  selector: 'app-create-contact-request',
  templateUrl: './create-contact-request.component.html',
  styleUrls: ['./create-contact-request.component.css']
})
export class CreateContactRequestComponent implements OnInit {

  contactRequestForm!: FormGroup;
  languages: Language[] = [];
  countries: Country[] = [];
  filteredCountries: Country[] = [];
  interestLevels: InterestLevel[] = [];
  counsellors: UserForBackend[] = [];
  salesAgents: SalesAgent[] = [];
  source = new ContactRequestSource(1, "manual", "Contact Request");
  schools: SimpleSchoolWithCountries[] = [];
  filteredSchools: SimpleSchoolWithCountries[] = [];
  leadTypes: LeadType[] = [];

  constructor(
    private builder: FormBuilder,
    private httpService: HttpService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.httpService.getAllCountries().subscribe(countries => {
      this.countries = countries;
      this.filteredCountries = countries;
      for (let country of this.countries) {
        if (country.name == "Belgium") {
          this.contactRequestForm.get("country")!.setValue(country);
        }
      }
    });
    this.httpService.getLeadTypes().subscribe(res => {
      this.leadTypes = res;
    });

    this.httpService.getSchoolsWithCountries().subscribe(res => {
      this.schools = res;
      this.filteredSchools = res;
    });

    this.httpService.getLanguages().subscribe(languages => {
      this.languages = languages;
      this.contactRequestForm.get("language")!.setValue(this.languages[0]);
    });

    this.httpService.getAllInterestLevels().subscribe(levels => {
      this.interestLevels = levels;
      this.contactRequestForm.get("interestLevel")!.setValue(this.interestLevels[1]);
    });

    this.httpService.getCounsellors().subscribe(res => {
      this.counsellors = res;
      this.setCounsellors();
    });

    this.httpService.getSalesAgents().subscribe(agents => {
      this.salesAgents = agents;
      this.contactRequestForm.get("salesAgent")!.setValue(this.salesAgents[0]);
    });

    this.contactRequestForm = this.builder.group({
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      email: null,
      phoneNumber: null,
      city: null,
      country: null,
      postCode: null,
      interestedIn: null,
      message: null,
      language: null,
      interestLevel: null,
      salesAgent: null,
      usersInCharge: this.builder.array([]),
      dateToContact: null,
      startDate: null,
      endDate: null,
      destination: null,
      school: null,
      birthDate: null,
      courseType: null,
      courseOption: null,
      appointmentType: null,
      leadType: null
    });
  }

  setCounsellors() {
    for (let counsellor of this.counsellors) {
      (this.contactRequestForm.controls["usersInCharge"] as FormArray).push(new FormControl(false));
    }
  }

  createContactRequest() {
    if(!this.contactRequestForm.value.leadType){
      this.contactRequestForm.get("leadType")!.setValue(this.leadTypes[0]);
    }
    let birthDate = null;
    if(this.contactRequestForm.value.birthDate){
      birthDate = moment(this.contactRequestForm.value.birthDate).utc(true);
    }
    let request = new ContactRequest(
      this.contactRequestForm.value.firstName,
      this.contactRequestForm.value.lastName,
      this.contactRequestForm.value.email,
      this.contactRequestForm.value.phoneNumber,
      this.contactRequestForm.value.city,
      this.contactRequestForm.value.country,
      this.contactRequestForm.value.postCode,
      this.contactRequestForm.value.interestedIn,
      this.contactRequestForm.value.message,
      "BE",
      null,
      this.contactRequestForm.value.language,
      false,
      this.contactRequestForm.value.interestLevel,
      this.source,
      this.contactRequestForm.value.leadType,
      moment(this.contactRequestForm.value.startDate).utc(true),
      moment(this.contactRequestForm.value.endDate).utc(true),
      this.contactRequestForm.value.destination,
      this.contactRequestForm.value.school,
      birthDate,
      null,
      null,
      this.contactRequestForm.value.courseType,
      this.contactRequestForm.value.courseOption,
      null,
      null,
      null,
      null,
      false,
      false,
      null,
      null,
      null,
      null,
      null,
      null,
      this.contactRequestForm.value.salesAgent,
      null,
      null,
      false,
      this.contactRequestForm.value.dateToContact == null ? null : moment(this.contactRequestForm.value.dateToContact).utc(true),
      this.getCheckedUsers()
    );
    this.httpService.postContactRequest(request).subscribe(() => {
      this.router.navigateByUrl("/management/leads");
    });
  }

  onSchoolUpdate(value: SimpleSchoolWithCountries){
    if(!this.contactRequestForm.value.school){
      return;
    }
    this.filteredCountries = [];
    for(let country of this.countries){
      for(let countryInSchool of value.countries){
        if(country.id === countryInSchool){
          this.filteredCountries.push(country);
        }
      }
    }
  }

  onDestinationUpdate(value: Country){
    if(!this.contactRequestForm.value.destination){
      return;
    }
    this.filteredSchools = [];
    for(let school of this.schools){
      for(let countryInSchool of school.countries){
        if(countryInSchool === value.id){
          this.filteredSchools.push(school);
        }
      }
    }
  }

  onSchoolClear(){
    if(this.contactRequestForm.value.school || this.contactRequestForm.value.destination){
      return;
    }
    this.filteredSchools = this.schools;
    this.filteredCountries = this.countries;
  }

  onDestinationClear(){
    if(this.contactRequestForm.value.school || this.contactRequestForm.value.destination){
      return;
    }
    this.filteredSchools = this.schools;
    this.filteredCountries = this.countries;
  }

  getCheckedUsers() {
    let newUsers = [];
    for (let j = 0; j < (this.contactRequestForm.controls["usersInCharge"] as FormArray).length; j++) {
      if ((this.contactRequestForm.controls["usersInCharge"] as FormArray).controls[j].value == true) {
        newUsers.push(this.counsellors[j]);
      }
    }
    return newUsers;
  }

  get usersFormArray(){
    let usersFormArray = this.contactRequestForm.get('usersInCharge') as FormArray;
    return usersFormArray.controls;
  }

}
